import React from 'react';
import { portalCopy } from '../copy';
import type { BusinessClass } from '../types';
import {
  MAX_BUSINESS_CLASSES,
  createEmptyBusinessClass,
  isBusinessClassComplete,
} from '../utils/businessClasses';
import { PortalButton, portalInputClass } from './PortalUi';

type WizardClassesEditorProps = {
  classes: BusinessClass[];
  onChange: (classes: BusinessClass[]) => void;
  /** When true, omits top divider — used inside profile tabs. */
  embedded?: boolean;
};

const compactInputClass =
  'w-full rounded-xl border border-[rgba(74,33,71,0.12)] bg-white px-3 py-2.5 font-jakarta text-[14px] text-[#2a0f2a] outline-none transition-colors placeholder:text-[#5a3158]/45 focus:border-chisme-primary/50 focus:ring-2 focus:ring-chisme-primary/15';

const compactTimeClass = `${compactInputClass} min-w-0`;

function RemoveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M3.5 3.5l7 7M10.5 3.5l-7 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

const WizardClassesEditor: React.FC<WizardClassesEditorProps> = ({
  classes,
  onChange,
  embedded = false,
}) => {
  const canAdd = classes.length < MAX_BUSINESS_CLASSES;
  const copy = portalCopy.wizard.profile.classes;

  const updateClass = (id: string, patch: Partial<BusinessClass>) => {
    onChange(classes.map((cls) => (cls.id === id ? { ...cls, ...patch } : cls)));
  };

  const removeClass = (id: string) => {
    onChange(classes.filter((cls) => cls.id !== id));
  };

  const addClass = () => {
    if (!canAdd) return;
    onChange([...classes, createEmptyBusinessClass()]);
  };

  return (
    <div className={embedded ? '' : 'border-t border-[rgba(74,33,71,0.08)] pt-6'}>
      <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 ${embedded ? 'mb-0' : ''}`}>
        {!embedded && (
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-jakarta text-sm font-semibold text-[#2a0f2a]">{copy.title}</h3>
              <span className="rounded-full bg-chisme-primary/10 px-2 py-0.5 font-jakarta text-[11px] font-bold text-chisme-primary">
                {copy.count(classes.length, MAX_BUSINESS_CLASSES)}
              </span>
            </div>
            <p className="mt-1 font-jakarta text-xs leading-relaxed text-[#5a3158]">{copy.subtitle}</p>
          </div>
        )}
        {embedded && (
          <p className="font-jakarta text-xs leading-relaxed text-[#5a3158]">{copy.subtitle}</p>
        )}
      </div>

      {classes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[rgba(74,33,71,0.18)] bg-[#fcf7fa] px-5 py-8 text-center">
          <p className="font-jakarta text-sm text-[#5a3158]">{copy.empty}</p>
          <PortalButton type="button" className="mt-4" onClick={addClass}>
            {copy.emptyAction}
          </PortalButton>
        </div>
      ) : (
        <ul className="space-y-3">
          {classes.map((cls, index) => {
            const hasName = isBusinessClassComplete(cls);

            return (
              <li
                key={cls.id}
                className="rounded-2xl border border-[rgba(74,33,71,0.1)] bg-[#fcf7fa] p-4 sm:p-5"
              >
                <div className="mb-4 flex items-center justify-between gap-3">
                  <span className="font-jakarta text-xs font-bold uppercase tracking-wide text-[#5a3158]">
                    {copy.itemLabel(index + 1)}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeClass(cls.id)}
                    aria-label={copy.removeAria(index + 1)}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-jakarta text-xs font-semibold text-[#5a3158] transition-colors hover:bg-white hover:text-chisme-primary"
                  >
                    <RemoveIcon />
                    {copy.remove}
                  </button>
                </div>

                <div>
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <label
                      htmlFor={`class-name-${cls.id}`}
                      className="font-jakarta text-sm font-semibold text-[#2a0f2a]"
                    >
                      {copy.name}
                    </label>
                    <span className="rounded-full bg-chisme-primary/10 px-2 py-0.5 font-jakarta text-[10px] font-semibold uppercase tracking-wide text-chisme-primary">
                      {copy.nameRequired}
                    </span>
                  </div>
                  <input
                    id={`class-name-${cls.id}`}
                    className={`${portalInputClass} bg-white ${!hasName ? 'border-chisme-primary/30' : ''}`}
                    value={cls.name}
                    onChange={(e) => updateClass(cls.id, { name: e.target.value })}
                    placeholder={copy.namePlaceholder}
                  />
                </div>

                <div className="mt-4 rounded-xl border border-[rgba(74,33,71,0.08)] bg-white/80 p-4">
                  <p className="mb-3 font-jakarta text-xs leading-relaxed text-[#5a3158]">
                    <span className="font-semibold text-[#2a0f2a]">{copy.optionalSection}</span>
                    <span className="text-[#5a3158]/70"> · </span>
                    <span>{copy.optionalHint}</span>
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor={`class-days-${cls.id}`}
                        className="mb-1.5 block font-jakarta text-xs font-medium text-[#5a3158]"
                      >
                        {copy.days}
                      </label>
                      <input
                        id={`class-days-${cls.id}`}
                        className={compactInputClass}
                        value={cls.days}
                        onChange={(e) => updateClass(cls.id, { days: e.target.value })}
                        placeholder={copy.daysPlaceholder}
                      />
                    </div>

                    <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2 sm:max-w-sm">
                      <div>
                        <label
                          htmlFor={`class-from-${cls.id}`}
                          className="mb-1.5 block font-jakarta text-xs font-medium text-[#5a3158]"
                        >
                          {copy.timeFrom}
                        </label>
                        <input
                          id={`class-from-${cls.id}`}
                          type="time"
                          className={compactTimeClass}
                          value={cls.from}
                          onChange={(e) => updateClass(cls.id, { from: e.target.value })}
                        />
                      </div>
                      <span className="pb-2.5 font-jakarta text-sm text-[#5a3158]/60">—</span>
                      <div>
                        <label
                          htmlFor={`class-to-${cls.id}`}
                          className="mb-1.5 block font-jakarta text-xs font-medium text-[#5a3158]"
                        >
                          {copy.timeTo}
                        </label>
                        <input
                          id={`class-to-${cls.id}`}
                          type="time"
                          className={compactTimeClass}
                          value={cls.to}
                          onChange={(e) => updateClass(cls.id, { to: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="sm:max-w-[140px]">
                      <label
                        htmlFor={`class-price-${cls.id}`}
                        className="mb-1.5 block font-jakarta text-xs font-medium text-[#5a3158]"
                      >
                        {copy.price}
                      </label>
                      <div className="relative">
                        <input
                          id={`class-price-${cls.id}`}
                          className={`${compactInputClass} pr-8`}
                          value={cls.price}
                          onChange={(e) => updateClass(cls.id, { price: e.target.value })}
                          placeholder={copy.pricePlaceholder}
                          inputMode="decimal"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 font-jakarta text-sm text-[#5a3158]/50">
                          {copy.priceSuffix}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {classes.length > 0 && classes.length < MAX_BUSINESS_CLASSES && (
        <PortalButton
          variant="ghost"
          fullWidth
          className="mt-3 !text-[13px] text-[#5a3158]"
          onClick={addClass}
        >
          + {copy.add}
        </PortalButton>
      )}
    </div>
  );
};

export default WizardClassesEditor;
